// @ts-check
/**
 * `DirectoryFolder` — a `FileSystemDirectoryHandle` as a Folder, and the
 * seekable sink over a file handle.
 *
 * **History.** This code lived in `helpers/storage.js` until 2026-09-06, when
 * it moved here under R11 — the Folder contract is this package's, and so are
 * the providers the package's own browser suite must import. A page under
 * `tests/browser/` loads the package and nothing above it; the helper now
 * re-exports what is defined here, so a consumer of the storage layer still
 * names one module. The code itself is unchanged by the move: only the import
 * of `../helpers.js` and this header differ.
 *
 * | Path | 32 MB, measured 2026-09-01 |
 * | --- | --- |
 * | Own storage, `createSyncAccessHandle` | 16–29 ms |
 * | Own storage, `createWritable` | 42–52 ms |
 * | A granted directory, `createWritable` | 291–308 ms |
 * | A granted directory, `createSyncAccessHandle` | impossible — `InvalidStateError` |
 *
 * ## Two things this module does that a naive one would not, and why
 *
 * **A write through a stream is positioned and truncated explicitly.** The
 * File System standard gives `createWritable()` swap-file semantics: the stream
 * writes into a copy, `close()` replaces the file, so the last close wins and
 * nothing is torn. Measured on 2026-09-06 across three engines, that holds
 * within one page everywhere — and across two pages of one origin it does not
 * hold everywhere: one engine's stream took its position from the file rather
 * than from the stream, so two tabs each writing 1000 bytes left 2000, and the
 * implicit truncation on close never happened. Writing at an explicit position
 * and truncating to exactly what was written leaves one writer's document,
 * whole, on all three. It costs nothing where the implicit behaviour was
 * already right, and it is the only form that is right everywhere, so it is the
 * form used. Nothing here asks which engine it is on.
 *
 * **A locked file is told apart from a medium with no fast path.** Both refuse
 * `createSyncAccessHandle`, and on one engine both refuse it with the same
 * name, `InvalidStateError` — which is the name this module reads to mean *"no
 * fast path here, fall back"*. Taken at face value, one writer holding a file
 * would make the folder downgrade itself permanently, and the other writer
 * would be told the wrong thing. They are distinguished by asking the medium
 * about a file nothing can be holding — a fresh name of the folder's own, under
 * `.probe/`, taken and removed — which is a question about the MEDIUM, not
 * about the file. A medium that has the fast path answers, and the first
 * refusal was therefore a lock: it is re-raised as `NoModificationAllowedError`,
 * the name the File System standard gives a locked file, on every engine. A
 * medium that has no fast path refuses that too, and the fallback is taken. The
 * answer is remembered per folder, so the question is asked at most once.
 *
 * @module @smotaal/pack-fs/folders/directory-folder
 * @author Saleh Abdel Motaal <dev@smotaal.io>
 * @ai Claude Opus 5 (code generation)
 */

import { NotFoundError } from '../errors.js';
import { isNotFound, segmentsOf } from '../helpers.js';

/** @typedef {import('../helpers.js').Entry} Entry */
/** @typedef {import('../helpers.js').Stat} Stat */
/** @typedef {import('../helpers.js').ByteRange} ByteRange */
/** @typedef {import('../helpers.js').Folder} Folder */
/** @typedef {import('../helpers.js').SeekableSink} SeekableSink */

// ========================================
// DirectoryFolder — a FileSystemDirectoryHandle
// ========================================

/**
 * Normalises what a caller may hand us into the one shape a sync access
 * handle accepts. `createWritable` takes all three; `write` on an access
 * handle does not take a `Blob`.
 *
 * @param {Uint8Array | Blob | ArrayBuffer} bytes
 * @returns {Promise<Uint8Array>}
 */
async function asBytes(bytes) {
    if (bytes instanceof Uint8Array) return bytes;
    if (bytes instanceof ArrayBuffer) return new Uint8Array(bytes);
    if (typeof Blob !== 'undefined' && bytes instanceof Blob)
        return new Uint8Array(await bytes.arrayBuffer());
    return new Uint8Array(/** @type {any} */ (bytes));
}

/**
 * How many bytes a value handed to a write stands for — asked without reading
 * it, so a `Blob` stays a reference.
 *
 * @param {Uint8Array | Blob | ArrayBuffer} bytes
 * @returns {number}
 */
function sizeOf(bytes) {
    if (ArrayBuffer.isView(bytes)) return bytes.byteLength;
    if (bytes instanceof ArrayBuffer) return bytes.byteLength;
    if (typeof Blob !== 'undefined' && bytes instanceof Blob) return bytes.size;
    return Number(/** @type {any} */ (bytes)?.byteLength ?? /** @type {any} */ (bytes)?.length ?? 0);
}

/**
 * Re-raises the browser's "not found" as the contract's, and everything else
 * as it was.
 *
 * @param {unknown} error
 * @param {string} at
 * @returns {never}
 */
function rethrow(error, at) {
    if (isNotFound(error)) throw new NotFoundError(at);
    throw error;
}

/**
 * Where the one-time question *"has this medium a fast path at all?"* is asked.
 * Created and removed within the call that asks; nothing is left behind.
 */
const PROBE_DIRECTORY = '.probe';

/** What a medium says when it has no synchronous access handle to give. */
const NO_FAST_PATH = 'InvalidStateError';

/** What the File System standard names a file another writer holds. */
const LOCKED = 'NoModificationAllowedError';

/** How long `cleanup` waits for a dead holder's open file to be let go. */
const RELEASE_BOUND_MS = 5000;

/**
 * The refusal a locked file must present as, whatever name the medium gave it.
 *
 * @param {string} at
 * @param {unknown} cause
 */
function lockedError(at, cause) {
    const error = new Error(
        `Another writer holds “${at}” — it cannot be written until that writer closes it`);
    error.name = LOCKED;
    /** @type {any} */ (error).path = at;
    /** @type {any} */ (error).cause = cause;
    return error;
}

/**
 * A `FileSystemDirectoryHandle` as a Folder: the browser's own storage, or a
 * directory the user granted through the browser's own picker.
 *
 * **The fast write path is built in.** `createWritable` is the only write
 * available on a granted folder; on the browser's own storage there is a
 * faster one, and the difference is not academic at 39–240 MB per file — the
 * table in this module's header has the numbers.
 *
 * The path is DISCOVERED on the first write rather than assumed, because one
 * class covers both media and only the first supports it — and
 * `createSyncAccessHandle` exists only in a worker. Once discovered it is
 * remembered, so the fallback costs one failed call per folder, not one per
 * write. `writePath` says which was settled on; nothing decides behaviour
 * from it.
 *
 * **`move` is a rename where the browser has one.** `FileSystemFileHandle.move`
 * renames in place, which is what makes a sibling-then-move write atomic;
 * elsewhere a move is a copy and a delete, and is honest about that.
 *
 * Two extras beyond the contract, for a medium that can hand out references:
 * `fileOf(at)` returns the disk-backed `File` (a 240 MB file can be streamed
 * without passing through JavaScript), and `directoryHandle` / `fileHandle`
 * expose the handles themselves for callers that need the Web API directly.
 * Neither exists on other providers, and nothing in this layer requires them.
 */
export class DirectoryFolder {
    /** @type {FileSystemDirectoryHandle} */
    #handle;

    #name;

    /**
     * `sync` once proven, `writable` once refused, `null` until the first
     * write settles it.
     *
     * @type {'sync' | 'writable' | null}
     */
    #writePath = null;

    /**
     * Whether this MEDIUM has a synchronous access handle to give at all —
     * `null` until something refuses one and the question has to be asked.
     * A property of the medium, not of any one file.
     *
     * @type {boolean | null}
     */
    #mediumHasFastPath = null;

    /** @param {{ handle: FileSystemDirectoryHandle, name?: string }} options */
    constructor({ handle, name }) {
        this.#handle = handle;
        this.#name = name ?? handle.name;
    }

    get kind() { return 'directory'; }
    get name() { return this.#name; }
    get handle() { return this.#handle; }

    /** Which write path this folder settled on. Diagnostics only. */
    get writePath() { return this.#writePath; }

    /**
     * A move here is a rename where the platform has one, and a copy then a
     * delete where it has not. Read from the Web API's own shape, so nothing
     * has to name a browser to know it.
     *
     * @returns {import('../helpers.js').MoveKind}
     */
    get moveKind() {
        const handleClass = /** @type {any} */ (globalThis).FileSystemFileHandle;
        return typeof handleClass?.prototype?.move === 'function' ? 'rename' : 'copy';
    }

    /**
     * Whether this medium can give a synchronous access handle at all, asked
     * of a file nothing can be holding: a fresh name of this folder's own,
     * taken and removed. Remembered, so it is asked at most once per folder.
     *
     * This is the question that tells a LOCKED file apart from a medium with
     * no fast path, where both refuse under one name — see this module's
     * header.
     *
     * @returns {Promise<boolean>}
     */
    async #mediumOffersFastPath() {
        if (this.#mediumHasFastPath !== null) return this.#mediumHasFastPath;

        const at = `${PROBE_DIRECTORY}/fast-path`;
        try {
            const handle = await this.fileHandle(at, { create: true });
            const access = await (/** @type {any} */ (handle).createSyncAccessHandle());
            access.close();
            this.#mediumHasFastPath = true;
        } catch (error) {
            const name = /** @type {any} */ (error)?.name;
            // A refusal of either name answers the question with "no". Anything
            // else is a fault of its own and is not swallowed — a medium that
            // cannot create a file at all has a bigger problem than this.
            if (name !== NO_FAST_PATH && name !== LOCKED) throw error;
            this.#mediumHasFastPath = false;
        } finally {
            // Its OWN file, and the directory only when nothing else is in it.
            // Taking `.probe/` recursively would take a scratch name another
            // probe is using at this moment — including the one whose refusal
            // brought us here, whose handle would then point at a file that no
            // longer exists.
            await this.remove(at).catch(() => {});
            await this.#removeProbeDirectoryIfEmpty();
        }
        return this.#mediumHasFastPath;
    }

    /**
     * Takes the scratch directory away once nothing is left in it, and leaves
     * it alone while anything is — so a folder is left as it was found without
     * a probe ever removing another probe's file.
     */
    async #removeProbeDirectoryIfEmpty() {
        try {
            if ((await this.list(PROBE_DIRECTORY)).length) return;
            await this.remove(PROBE_DIRECTORY, { recursive: true });
        } catch {
            // It was not there, or something else is using it. Either way there
            // is nothing to tidy and nothing to report: the caller's operation
            // is what matters, not the housekeeping after it.
        }
    }

    /**
     * What to do with a refusal of `createSyncAccessHandle`: return, and the
     * caller falls back to the stream; or throw, having named the refusal
     * properly.
     *
     * @param {unknown} error
     * @param {string} at
     * @returns {Promise<void>}
     */
    async #refuseOrFallBack(error, at) {
        // Once the fast path has been proven for this folder, a later failure
        // is a real fault — falling back would hide it.
        if (this.#writePath === 'sync') throw error;

        const name = /** @type {any} */ (error)?.name;
        // Already the standard's name for a held file: nothing to decide.
        if (name === LOCKED) throw error;
        // Anything but "no fast path" is a fault of its own.
        if (name !== NO_FAST_PATH) throw error;
        // The ambiguous one. If the medium has the fast path, this file is
        // held by somebody, and saying "no fast path" would be false.
        if (await this.#mediumOffersFastPath()) throw lockedError(at, error);
    }

    /**
     * The browser's own storage, or a subdirectory of it.
     *
     * @param {{ subdirectory?: string }} [options]
     */
    static async openOPFS({ subdirectory } = {}) {
        if (!globalThis.navigator?.storage?.getDirectory)
            throw new Error('This browser has no origin private file system — navigator.storage.getDirectory is missing');
        let handle = await navigator.storage.getDirectory();
        if (subdirectory) {
            for (const part of segmentsOf(subdirectory)) handle = await handle.getDirectoryHandle(part, { create: true });
        }
        return new DirectoryFolder({ handle, name: subdirectory ?? 'OPFS' });
    }

    // ========================================
    // Temporary folders held by Web Locks
    // ========================================

    /**
     * The Web Lock's name for a path of this folder: the folder's name and
     * the path, so a lock held on it names the folder it owns (the developer,
     * 2026-11-01: "the lock pointing to the path of the temporary folder").
     * A run holds `navigator.locks.request(lockNameOf(at), { mode:
     * 'exclusive' }, () => <the run's end>)` for its life; the browser
     * releases it when the tab or worker that holds it is gone.
     *
     * @param {string} at
     */
    lockNameOf(at) {
        return `${this.#name}/${at}`;
    }

    /**
     * The cleanup (the developer's word, 2026-11-01): every directory under
     * `at` whose lock nobody holds is removed, since the run that owned it
     * ended or its tab crashed, and the browser dropped the lock either way;
     * a directory whose lock is held is kept. No state is tracked: the locks
     * are the ledger, read with `navigator.locks.query()`.
     *
     * @param {string} [at] the directory whose subdirectories are the temporary folders
     * @returns {Promise<{ removed: string[], kept: string[] }>}
     */
    async cleanup(at = '') {
        const locks = /** @type {any} */ (globalThis.navigator)?.locks;
        if (typeof locks?.query !== 'function') {
            throw new Error('The cleanup needs navigator.locks.query() to know which folders are held');
        }
        const { held } = await locks.query();
        const heldNames = new Set(held.map((/** @type {{ name: string }} */ lock) => lock.name));
        /** @type {string[]} */
        const removed = [];
        /** @type {string[]} */
        const kept = [];
        for (const entry of await this.list(at)) {
            if (entry.kind !== 'directory') continue;
            const path = at ? `${at}/${entry.name}` : entry.name;
            if (heldNames.has(this.lockNameOf(path))) {
                kept.push(path);
                continue;
            }
            // A file the dead holder's worker had open through a synchronous
            // access handle stays locked a moment after the lock itself is
            // released (measured 2026-11-01 in Chromium and Firefox: 25 to
            // 65 ms, the removal refused as LOCKED): that refusal is the
            // condition asked about, and the removal is tried again until it
            // passes or the bound is reached, when the refusal is thrown.
            const startedAt = Date.now();
            for (; ;) {
                try {
                    await this.remove(path, { recursive: true });
                    break;
                } catch (error) {
                    if (/** @type {any} */ (error)?.name !== LOCKED || Date.now() - startedAt > RELEASE_BOUND_MS) throw error;
                }
                await new Promise(resolve => setTimeout(resolve, 25));
            }
            removed.push(path);
        }
        return { removed, kept };
    }

    // ========================================
    // Handles
    // ========================================

    /**
     * The handle of a directory, creating the way to it when asked.
     *
     * @param {string} at
     * @param {{ create?: boolean }} [options]
     * @returns {Promise<FileSystemDirectoryHandle>}
     */
    async directoryHandle(at = '', { create = false } = {}) {
        let handle = this.#handle;
        try {
            for (const part of segmentsOf(at)) handle = await handle.getDirectoryHandle(part, { create });
        } catch (error) {
            rethrow(error, at);
        }
        return handle;
    }

    /**
     * The handle of a file, creating it and the way to it when asked.
     *
     * @param {string} at
     * @param {{ create?: boolean }} [options]
     * @returns {Promise<FileSystemFileHandle>}
     */
    async fileHandle(at, { create = false } = {}) {
        const parts = segmentsOf(at);
        if (!parts.length) throw new TypeError('A file path is required');
        const name = /** @type {string} */ (parts.pop());
        const directory = await this.directoryHandle(parts.join('/'), { create });
        try {
            return await directory.getFileHandle(name, { create });
        } catch (error) {
            return rethrow(error, at);
        }
    }

    /**
     * The stored file itself, for streaming — disk-backed, so it or a slice of
     * it can be handed to `fetch`, an archive or a writer without its bytes
     * passing through JavaScript.
     *
     * @param {string} at
     * @returns {Promise<File>}
     */
    async fileOf(at) {
        return (await this.fileHandle(at)).getFile();
    }

    // ========================================
    // The seven calls
    // ========================================

    /** @param {string} [at] */
    async list(at = '') {
        const directory = await this.directoryHandle(at);
        /** @type {Entry[]} */
        const entries = [];
        for await (const [name, handle] of /** @type {any} */ (directory).entries()) {
            if (handle.kind === 'directory') {
                entries.push({ name, kind: 'directory', size: 0, modifiedAt: 0 });
                continue;
            }
            const file = await handle.getFile();
            entries.push({ name, kind: 'file', size: file.size, modifiedAt: file.lastModified });
        }
        return entries;
    }

    /** @param {string} at */
    async stat(at) {
        try {
            const file = await (await this.fileHandle(at)).getFile();
            return { kind: /** @type {const} */ ('file'), size: file.size, modifiedAt: file.lastModified };
        } catch (error) {
            // A directory answers `getFileHandle` with TypeMismatchError, and
            // a missing entry with NotFoundError; either way the directory
            // question is asked next, and ITS "not found" is the answer.
            if (!isNotFound(error) && /** @type {any} */ (error)?.name !== 'TypeMismatchError') throw error;
            await this.directoryHandle(at);
            return { kind: /** @type {const} */ ('directory'), size: 0, modifiedAt: 0 };
        }
    }

    /**
     * @param {string} at
     * @param {ByteRange} [range]
     * @returns {Promise<Uint8Array>}
     */
    async readFile(at, range) {
        const file = await (await this.fileHandle(at)).getFile();
        const slice = range && Number.isFinite(range.start)
            ? file.slice(range.start, range.end === undefined ? file.size : range.end + 1)
            : file;
        return new Uint8Array(await slice.arrayBuffer());
    }

    /**
     * @param {string} at
     * @param {Uint8Array | Blob | ArrayBuffer} bytes
     */
    async writeFile(at, bytes) {
        const handle = await this.fileHandle(at, { create: true });

        if (this.#writePath !== 'writable'
            && typeof (/** @type {any} */ (handle).createSyncAccessHandle) === 'function') {
            /** @type {any} */
            let access = null;
            try {
                access = await (/** @type {any} */ (handle).createSyncAccessHandle());
            } catch (error) {
                // Returns to fall back; throws when the refusal was a lock, or
                // was never about the fast path at all.
                await this.#refuseOrFallBack(error, at);
            }
            if (access) {
                // The bytes are materialised only now that the fast path is in
                // hand: a `Blob` asked for earlier would have been read into
                // memory whole even where the handle was about to be refused,
                // which at 240 MB a member is the difference that matters.
                try {
                    const view = await asBytes(bytes);
                    access.truncate(0);
                    access.write(view, { at: 0 });
                    access.flush();
                } finally {
                    access.close();
                }
                this.#writePath ??= 'sync';
                return this.stat(at);
            }
        }

        this.#writePath = 'writable';
        const writable = await handle.createWritable();
        try {
            // Positioned and truncated explicitly rather than left to the
            // stream's own cursor and its implicit truncation on close — the
            // only form that leaves one writer's document WHOLE on every
            // engine when two writers of one origin share a file. See this
            // module's header. A `Blob` passes through as a reference: the
            // medium copies from it, and its bytes never enter JavaScript.
            const size = sizeOf(bytes);
            await writable.write(/** @type {any} */ ({ type: 'write', position: 0, data: bytes }));
            await writable.truncate(size);
        } catch (error) {
            // A stream left open holds the swap file and the entry stays
            // unwritten; aborting releases both.
            await writable.abort?.().catch(() => {});
            throw error;
        }
        await writable.close();
        return this.stat(at);
    }

    /**
     * A sink that writes at an offset — the append protocol's need, by
     * whichever path this medium has.
     *
     * The exclusive handle where the medium offers one (a worker, the
     * browser's own storage); a stream with positional writes everywhere else.
     * **Nothing is refused**: a granted directory and the main thread append
     * more slowly, they do not fail (`USE-CASES.md` §1, P3).
     *
     * @param {string} at
     * @returns {Promise<SeekableSink>}
     */
    async seekableSink(at) {
        const handle = await this.fileHandle(at, { create: true });

        if (this.#writePath !== 'writable'
            && typeof (/** @type {any} */ (handle).createSyncAccessHandle) === 'function') {
            try {
                return await syncAccessSink(handle);
            } catch (error) {
                await this.#refuseOrFallBack(error, at);
            }
        }
        return writableSink(handle);
    }

    /** @param {string} at */
    async makeDirectory(at) {
        await this.directoryHandle(at, { create: true });
        return { kind: /** @type {const} */ ('directory'), size: 0, modifiedAt: 0 };
    }

    /** @param {string} at @param {{ recursive?: boolean }} [options] */
    async remove(at, { recursive = false } = {}) {
        const parts = segmentsOf(at);
        if (!parts.length) throw new Error('Refusing to remove the folder itself');
        const name = /** @type {string} */ (parts.pop());
        const directory = await this.directoryHandle(parts.join('/'));
        try {
            await directory.removeEntry(name, { recursive });
        } catch (error) {
            rethrow(error, at);
        }
        return { removed: at };
    }

    /**
     * A rename where the browser has `FileSystemFileHandle.move` — one
     * operation, no moment where the file is gone — and a copy then a delete
     * elsewhere.
     *
     * @param {string} from
     * @param {string} to
     */
    async move(from, to) {
        const source = await this.fileHandle(from);
        const toParts = segmentsOf(to);
        if (!toParts.length) throw new TypeError('A destination file path is required');
        const toName = /** @type {string} */ (toParts.pop());
        const destination = await this.directoryHandle(toParts.join('/'), { create: true });

        if (typeof (/** @type {any} */ (source).move) === 'function') {
            try {
                await /** @type {any} */ (source).move(destination, toName);
                return this.stat(to);
            } catch (error) {
                // A handle that cannot be moved this way — a granted folder,
                // an older browser — falls back to the copy; a real fault
                // (the source gone) does not.
                if (isNotFound(error)) rethrow(error, from);
            }
        }

        const bytes = await this.readFile(from);
        await this.writeFile(to, bytes);
        await this.remove(from);
        return this.stat(to);
    }
}

/**
 * A seekable sink over a file handle, for writing at an offset.
 *
 * `createSyncAccessHandle` needs a worker and exists only for the browser's
 * own storage, and it is the fastest write there that takes an offset — an
 * append protocol cannot run without one of the two.
 *
 * @param {FileSystemFileHandle} handle
 * @returns {Promise<SeekableSink>}
 */
export async function syncAccessSink(handle) {
    const access = await /** @type {any} */ (handle).createSyncAccessHandle();
    return {
        kind: /** @type {const} */ ('sync'),
        /** @param {Uint8Array} bytes @param {number} at */
        write(bytes, at) { access.write(bytes, { at }); },
        /** @param {number} size */
        truncate(size) { access.truncate(size); },
        size() { return access.getSize(); },
        close() { access.flush(); access.close(); },
    };
}

/**
 * A seekable sink over a file handle that writes at an offset through
 * `createWritable` — the path the main thread and a granted directory take.
 *
 * `keepExistingData` is what makes this an APPEND rather than a rewrite:
 * without it the swap file starts empty and every byte before the append point
 * would be lost.
 *
 * The stream is opened on the first call rather than here, so the swap file is
 * held for as short a time as possible and a sink nobody writes to costs
 * nothing.
 *
 * **`size()` answers with the file as STORED.** These streams have swap-file
 * semantics: what they write appears when they are closed, not before. So
 * during an unclosed run of writes this reports the size before them. Nothing
 * in the writer asks a sink for its size mid-write, and the honest answer is
 * given rather than a remembered one.
 *
 * @param {FileSystemFileHandle} handle
 * @returns {SeekableSink}
 */
export function writableSink(handle) {
    /** @type {Promise<any> | null} */
    let opening = null;
    /**
     * Whether a write or a truncate has already failed on this stream.
     *
     * **A close must never speak over the failure that caused it.** A stream
     * whose write errored cannot be closed — the attempt raises *"Cannot close
     * a writable stream that is closed or errored"* — and a caller doing the
     * ordinary thing, writing in a `try` and closing in a `finally`, would then
     * see that message instead of the one that says what actually went wrong.
     * Measured 2026-09-06: a scale run reported the close failure at member 8
     * of 16 and the real cause never reached the report at all.
     */
    let failed = false;
    const ready = () => (opening ??= /** @type {any} */ (handle).createWritable({ keepExistingData: true }));

    return {
        kind: /** @type {const} */ ('positional'),
        /** @param {Uint8Array} bytes @param {number} at */
        async write(bytes, at) {
            const stream = await ready();
            try {
                await stream.write({ type: 'write', position: at, data: bytes });
            } catch (error) {
                failed = true;
                throw error;
            }
        },
        /** @param {number} size */
        async truncate(size) {
            const stream = await ready();
            try {
                await stream.truncate(size);
            } catch (error) {
                failed = true;
                throw error;
            }
        },
        async size() {
            return (await handle.getFile()).size;
        },
        async close() {
            if (!opening) return;
            const stream = await opening;
            opening = null;
            if (failed) {
                // Release the swap file and say nothing: whatever went wrong
                // was already raised by the call it went wrong in, and that is
                // the error the caller needs.
                await stream.abort?.().catch(() => {});
                return;
            }
            await stream.close();
        },
    };
}
